import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { useAuth } from '../context/AuthContext.js';
import { useCheckRun, useOrg, useRunPhotos } from '../lib/queries.js';
import type { CheckItemInRun, RunPhoto } from '../lib/queries.js';
import { actorLabel } from '../lib/people.js';
import { assetTypeLabel } from '../lib/asset-types.js';
import { checkRunCsv, downloadText, reportFilename } from '../lib/export.js';
import type { CheckStatus } from '@keetcheck/shared';

// Printed on white paper whatever the app theme is.
const INK = '#1d2230', MUTED = '#6b6e78', RULE = '#dcdcd8';
const STATUS: Record<CheckStatus, { label: string; bg: string; fg: string }> = {
  ok:        { label: 'OK',        bg: '#e4f0e6', fg: '#356b41' },
  missing:   { label: 'Missing',   bg: '#f4dadc', fg: '#8a3035' },
  broken:    { label: 'Broken',    bg: '#f4dadc', fg: '#8a3035' },
  attention: { label: 'Attention', bg: '#f5ead0', fg: '#735012' },
  rectified: { label: 'Rectified', bg: '#dfe4f0', fg: '#3b4a76' },
};
const isIssue = (i: CheckItemInRun) => i.flagged || (i.status != null && !['ok', 'rectified'].includes(i.status));

/**
 * Printable inspection report — "Save as PDF" from the browser's print
 * dialog produces the PDF (works on phones too). Opened with ?print=1 it
 * prints automatically once every photo has loaded.
 */
export function ReportPage() {
  const { runId = '' } = useParams<{ runId: string }>();
  const [params] = useSearchParams();
  const back = useBack(`/checks/${runId}`);
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: run } = useCheckRun(orgId, runId);
  const { data: photos = [], isSuccess: photosReady } = useRunPhotos(orgId, runId);
  const { data: org } = useOrg(orgId);

  const photosByItem = useMemo(() => {
    const m = new Map<string, RunPhoto[]>();
    for (const p of photos) m.set(p.itemId, [...(m.get(p.itemId) ?? []), p]);
    return m;
  }, [photos]);

  const sections = useMemo(() => {
    const m = new Map<string, { name: string; order: number; items: CheckItemInRun[] }>();
    for (const i of run?.items ?? []) {
      const key = i.sectionName ?? 'General';
      const s = m.get(key) ?? { name: key, order: i.sectionSortOrder ?? -1, items: [] };
      s.items.push(i);
      m.set(key, s);
    }
    return [...m.values()]; // items arrive in check order (section tree)
  }, [run]);

  // Auto-print once, after the data and every photo have loaded.
  const [loaded, setLoaded] = useState(0);
  const printed = useRef(false);
  const ready = !!run && photosReady && loaded >= photos.length;
  useEffect(() => {
    if (ready && params.get('print') === '1' && !printed.current) {
      printed.current = true;
      setTimeout(() => window.print(), 300);
    }
  }, [ready, params]);

  if (!run) return <div style={{ padding: 24, fontFamily: 'Inter, sans-serif', color: MUTED }}>Preparing report…</div>;

  const issues = run.items.filter(isIssue);
  const counts = {
    total: run.items.length,
    ok: run.items.filter((i) => i.status === 'ok' || i.status === 'rectified').length,
    issues: issues.length,
    unchecked: run.items.filter((i) => i.status == null).length,
  };
  const onImg = () => setLoaded((n) => n + 1);

  return (
    <div className="report" style={{ colorScheme: 'light', background: '#fff', color: INK, fontFamily: 'Inter, system-ui, sans-serif', minHeight: '100%' }}>
      {/* toolbar — not printed */}
      <div className="no-print" style={{
        position: 'sticky', top: 0, zIndex: 5, display: 'flex', gap: 8, alignItems: 'center',
        padding: '10px 16px', background: '#f7f6f4', borderBottom: `1px solid ${RULE}`,
      }}>
        <button onClick={back} style={tbBtn}>← Back</button>
        <div style={{ flex: 1, fontSize: 12, color: MUTED }}>
          {ready ? 'Choose “Save as PDF” as the printer to get a PDF.' : `Loading photos… (${Math.min(loaded, photos.length)}/${photos.length})`}
        </div>
        <button onClick={() => downloadText(reportFilename(run, 'csv'), checkRunCsv(run))} style={tbBtn}>CSV</button>
        <button onClick={() => window.print()} style={{ ...tbBtn, background: '#4d5bd1', color: '#fff', border: 'none' }}>
          Save as PDF / Print
        </button>
      </div>

      <main style={{ maxWidth: 820, margin: '0 auto', padding: '24px 24px 40px' }}>
        {/* header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, borderBottom: `2px solid ${INK}`, paddingBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {org?.name ?? 'MusterTap'} · Inspection report
            </div>
            <h1 style={{ margin: '4px 0 0', fontSize: 24 }}>{run.assetName}</h1>
            <div style={{ fontSize: 13, color: MUTED }}>{assetTypeLabel(run.assetType)}</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
            <div><b style={{ color: INK }}>{run.scheduleId ? 'Scheduled check' : 'On-demand check'}</b></div>
            <div>{run.periodStart.slice(0, 10) === run.periodEnd.slice(0, 10)
              ? fmtDate(run.periodStart)
              : `${fmtDate(run.periodStart)} – ${fmtDate(run.periodEnd)}`}</div>
            <div>Team: {run.teamName ?? 'Any crew (unassigned)'}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: MUTED, margin: '12px 0' }}>
          <span>Status: <b style={{ color: INK }}>{run.status === 'submitted' ? 'Submitted' : 'In progress (not submitted)'}</b></span>
          {run.submittedAt && (
            <span>Submitted by <b style={{ color: INK }}>{actorLabel(run.submittedByName, run.submittedByActive)}</b> on {fmtDateTime(run.submittedAt)}</span>
          )}
        </div>
        {run.submissionNote && (
          <div style={{ fontSize: 13, padding: '10px 12px', background: '#f7f6f4', borderRadius: 6, marginBottom: 12 }}>
            <b>Summary note:</b> {run.submissionNote}
          </div>
        )}

        {/* counts */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, margin: '12px 0 20px' }}>
          <Count label="Items" value={counts.total} />
          <Count label="OK / rectified" value={counts.ok} />
          <Count label="Issues" value={counts.issues} warn={counts.issues > 0} />
          <Count label="Not checked" value={counts.unchecked} warn={counts.unchecked > 0} />
        </div>

        {issues.length > 0 && (
          <section>
            <h2 style={h2}>Issues ({issues.length})</h2>
            {issues.map((i) => <ItemRow key={i.id} item={i} photos={photosByItem.get(i.id)} onImg={onImg} showSection />)}
          </section>
        )}

        <section>
          <h2 style={h2}>All items</h2>
          {sections.map((s) => (
            <div key={s.name} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 0 4px' }}>
                {s.name}
              </div>
              {/* issue photos are already shown above; don't print them twice */}
              {s.items.map((i) => <ItemRow key={i.id} item={i} photos={isIssue(i) ? undefined : photosByItem.get(i.id)} onImg={onImg} />)}
            </div>
          ))}
        </section>

        <div style={{ marginTop: 24, fontSize: 10, color: MUTED, borderTop: `1px solid ${RULE}`, paddingTop: 8 }}>
          Generated {fmtDateTime(new Date().toISOString())} · MusterTap
        </div>
      </main>
    </div>
  );
}

function ItemRow({ item, photos, onImg, showSection }: {
  item: CheckItemInRun; photos: RunPhoto[] | undefined; onImg: () => void; showSection?: boolean;
}) {
  const st = item.status ? STATUS[item.status] : null;
  return (
    <div className="avoid-break" style={{ display: 'flex', gap: 12, padding: '8px 0', borderTop: `1px solid ${RULE}` }}>
      <div style={{ width: 88, flexShrink: 0 }}>
        <span style={{
          display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
          background: st?.bg ?? '#ececea', color: st?.fg ?? MUTED,
        }}>{st?.label ?? 'Not checked'}</span>
        {item.flagged && <div style={{ fontSize: 10, color: '#8a3035', fontWeight: 700, marginTop: 3 }}>⚑ FLAGGED</div>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {item.equipmentName}{item.serialNumber ? <span style={{ color: MUTED, fontWeight: 400 }}> #{item.serialNumber}</span> : null}
        </div>
        {showSection && item.sectionName && <div style={{ fontSize: 11, color: MUTED }}>{item.sectionName}</div>}
        {(item.expected || item.reading) && (
          <div style={{ fontSize: 12, marginTop: 2 }}>
            {item.expected && !/^check$/i.test(item.expected.trim()) && <span style={{ color: MUTED }}>Expected {item.expected}</span>}
            {item.reading && <span style={{ marginLeft: item.expected ? 10 : 0 }}>Reading: <b>{item.reading}</b></span>}
          </div>
        )}
        {item.note && <div style={{ fontSize: 12, marginTop: 2 }}>{item.note}</div>}
        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
          {[
            item.externalRef && `Ref ${item.externalRef}`,
            item.checkedByName && actorLabel(item.checkedByName, item.checkedByActive),
            item.checkedAt && fmtDateTime(item.checkedAt),
          ].filter(Boolean).join(' · ')}
        </div>
        {!!photos?.length && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {photos.map((p) => (
              <img key={p.id} src={p.url} alt={p.caption ?? item.equipmentName ?? 'photo'} onLoad={onImg} onError={onImg}
                style={{ width: 140, height: 105, objectFit: 'cover', borderRadius: 4, border: `1px solid ${RULE}` }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Count({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div style={{ border: `1px solid ${RULE}`, borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: warn ? '#8a3035' : INK }}>{value}</div>
      <div style={{ fontSize: 11, color: MUTED }}>{label}</div>
    </div>
  );
}

const h2: React.CSSProperties = { fontSize: 15, margin: '20px 0 4px', paddingBottom: 4, borderBottom: `1px solid ${INK}` };
const tbBtn: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${RULE}`, background: '#fff',
  color: INK, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
